// src/components/common/ChequeDetailsFields/ChequeDetailsFields.jsx
//
// Cheque details for a cheque payment: a themed "Post-dated cheque" checkbox. When ticked,
// a cheque date strictly after today is required (the backend re-checks this against the
// business timezone).
import React from 'react';
import { Check, CalendarClock } from 'lucide-react';
import DatePicker from '../../ui/DatePicker/DatePicker';
import { useLocalization } from '../../../contexts/LocalizationContext';
import styles from './ChequeDetailsFields.module.css';

const toYMD = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

export const tomorrowYMD = () => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return toYMD(d);
};

export const emptyCheque = () => ({
  isPostDated: false,
  chequeDate: '',
});

// Returns a translation key for the first problem, or null when valid.
export const getChequeErrorKey = (cheque) => {
  if (!cheque?.isPostDated) return null;
  if (!cheque.chequeDate) return 'offlineSales.chqDateRequired';
  if (cheque.chequeDate < tomorrowYMD()) return 'offlineSales.chqDateMustBeFuture';
  return null;
};

export const toChequePayload = (cheque) => ({
  isPostDated: Boolean(cheque?.isPostDated),
  chequeDate: cheque?.isPostDated ? cheque.chequeDate : null,
});

const ChequeDetailsFields = ({ value, onChange, idPrefix = 'chq', showErrors = false }) => {
  const { t } = useLocalization();
  const cheque = value || emptyCheque();
  const errorKey = getChequeErrorKey(cheque);
  const showDateError = Boolean(errorKey) && (showErrors || Boolean(cheque.chequeDate));

  const togglePostDated = (checked) => {
    onChange({ ...cheque, isPostDated: checked, chequeDate: checked ? cheque.chequeDate : '' });
  };

  return (
    <div className={styles.chequeBox}>
      <div className={styles.chequeTitle}>{t('offlineSales.chqDetails')}</div>

      <label className={styles.checkboxRow} htmlFor={`${idPrefix}-postdated`}>
        <input
          id={`${idPrefix}-postdated`}
          type="checkbox"
          className={styles.checkboxInput}
          checked={Boolean(cheque.isPostDated)}
          onChange={(e) => togglePostDated(e.target.checked)}
        />
        <span className={styles.checkboxBox} aria-hidden="true">
          <Check size={12} strokeWidth={3} />
        </span>
        <span className={styles.checkboxText}>
          <span className={styles.checkboxLabel}>{t('offlineSales.chqPostDated')}</span>
          <span className={styles.checkboxHint}>{t('offlineSales.chqPostDatedHint')}</span>
        </span>
      </label>

      {cheque.isPostDated && (
        <div className={styles.field}>
          <label>
            <CalendarClock size={13} /> {t('offlineSales.chqDate')} <span className={styles.required}>*</span>
          </label>
          <DatePicker
            value={cheque.chequeDate}
            onChange={(v) => onChange({ ...cheque, chequeDate: v })}
            minDate={tomorrowYMD()}
            placeholder={t('offlineSales.chqSelectDate')}
            className={showDateError ? styles.datePickerError : ''}
          />
          {showDateError && <span className={styles.errorText}>{t(errorKey)}</span>}
        </div>
      )}
    </div>
  );
};

export default ChequeDetailsFields;
